import type { BulkResolution } from '@/lib/bulk-add/bulk-session';
// Modal for resolving one pairwise conflict (batch-vs-batch or batch-vs-DB).
// Three options: Fusionner (merge — hidden when the batch is indéterminé, since
// there is no noyau count to bump), Garder les deux (keep both as separate
// words), and Supprimer l'un (exclude one — for a DB conflict this is fixed to
// "ignorer le nouveau", as the original is never deleted).
import type { BulkConflict } from '@/lib/bulk-add/conflicts';
import type { NoyauCode } from '@/schemas/word';
import { useState } from 'react';
import { createPortal } from 'react-dom';

interface BulkConflictDialogProps {
  conflict: BulkConflict;
  /** The batch noyau (null = indéterminé → no Fusionner option). */
  noyau: NoyauCode | null;
  onResolve: (resolution: BulkResolution) => void;
  onClose: () => void;
}

/** Conflict-resolution modal with the three resolution options. */
export function BulkConflictDialog({
  conflict,
  noyau,
  onResolve,
  onClose,
}: BulkConflictDialogProps) {
  // 'choose' shows the three options; 'delete' shows the which-to-remove picker.
  const [mode, setMode] = useState<'choose' | 'delete'>('choose');
  const isDb = conflict.kind === 'db';

  // The two titles in play, plus the removable target for batch-vs-batch delete.
  const titleA = conflict.kind === 'batch' ? conflict.a.title : conflict.word.title;
  const titleB = conflict.kind === 'batch' ? conflict.b.title : conflict.dbWord.title;
  const [deleteId, setDeleteId] = useState<string>(
    conflict.kind === 'batch' ? conflict.a.id : conflict.word.id,
  );

  function handleMerge() {
    if (conflict.kind === 'batch') {
      onResolve({ kind: 'mergeBatch', keepId: conflict.a.id, dropId: conflict.b.id });
    } else {
      onResolve({
        kind: 'mergeDb',
        wordId: conflict.word.id,
        dbId: conflict.dbWord.id,
        dbTitle: conflict.dbWord.title,
        dbTags: conflict.dbWord.tags,
      });
    }
  }

  function handleDelete() {
    const wordId = isDb ? (conflict as Extract<BulkConflict, { kind: 'db' }>).word.id : deleteId;
    onResolve({ kind: 'deleteOne', wordId });
  }

  const mergeCopy = isDb
    ? `Fusionner avec l'original — ses tags sont ajoutés${noyau ? ` et +1 au noyau ${noyau} de l'original` : ''}.`
    : `Fusionner en un seul mot — les tags des deux mots sont conservés${noyau ? ', pondération du noyau réglée à 2' : ''}.`;

  const dialog = (
    <dialog
      open
      className="bulk-overlay"
      aria-modal="true"
      aria-label="Résoudre le conflit"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="bulk-dialog">
        <h2 className="bulk-dialog__title">Résoudre le conflit</h2>
        <p className="bulk-dialog__lead">Deux mots portent le même titre&nbsp;:</p>

        <div className="bulk-dialog__pair">
          <span className="bulk-dialog__word">{titleA}</span>
          <span className="bulk-dialog__vs" aria-hidden="true">
            ↔
          </span>
          <span className="bulk-dialog__word">
            {titleB}
            {isDb && <span className="bulk-dialog__badge">Original (base)</span>}
          </span>
        </div>

        {mode === 'choose' ? (
          <div className="bulk-dialog__options">
            {noyau && (
              <button type="button" className="bulk-dialog__option" onClick={handleMerge}>
                <span className="bulk-dialog__option-name">Fusionner</span>
                <span className="bulk-dialog__option-desc">{mergeCopy}</span>
              </button>
            )}
            <button
              type="button"
              className="bulk-dialog__option"
              onClick={() => onResolve({ kind: 'keepBoth', key: conflict.key })}
            >
              <span className="bulk-dialog__option-name">Garder les deux</span>
              <span className="bulk-dialog__option-desc">
                Les deux mots sont ajoutés séparément.
              </span>
            </button>
            <button
              type="button"
              className="bulk-dialog__option"
              onClick={() => (isDb ? handleDelete() : setMode('delete'))}
            >
              <span className="bulk-dialog__option-name">Supprimer l'un</span>
              <span className="bulk-dialog__option-desc">
                {isDb
                  ? 'Ignorer le nouveau mot (l’original est conservé).'
                  : 'Retirer un des deux mots.'}
              </span>
            </button>
          </div>
        ) : (
          <fieldset className="bulk-dialog__delete">
            <legend className="bulk-dialog__option-name">Quel mot retirer&nbsp;?</legend>
            {conflict.kind === 'batch' &&
              [conflict.a, conflict.b].map((w) => (
                <label key={w.id} className="bulk-dialog__radio">
                  <input
                    type="radio"
                    name="bulk-delete-target"
                    checked={deleteId === w.id}
                    onChange={() => setDeleteId(w.id)}
                  />
                  {w.title}
                </label>
              ))}
            <div className="bulk-dialog__actions">
              <button
                type="button"
                className="bulk-btn bulk-btn--ghost"
                onClick={() => setMode('choose')}
              >
                Retour
              </button>
              <button type="button" className="bulk-btn bulk-btn--danger" onClick={handleDelete}>
                Retirer ce mot
              </button>
            </div>
          </fieldset>
        )}

        {mode === 'choose' && (
          <div className="bulk-dialog__actions">
            <button type="button" className="bulk-btn bulk-btn--ghost" onClick={onClose}>
              Annuler
            </button>
          </div>
        )}
      </div>
    </dialog>
  );

  return createPortal(dialog, document.body);
}
